import React, { useEffect, useCallback } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { $getRoot, $getSelection, SELECTION_CHANGE_COMMAND, FORMAT_TEXT_COMMAND, FORMAT_ELEMENT_COMMAND, UNDO_COMMAND, REDO_COMMAND, INDENT_CONTENT_COMMAND, OUTDENT_CONTENT_COMMAND, COMMAND_PRIORITY_CRITICAL, COMMAND_PRIORITY_EDITOR, $createParagraphNode, $createTextNode, $isRangeSelection } from "lexical";
import { $isLinkNode, $toggleLink, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListNode, ListItemNode } from "@lexical/list";
import { $createHeadingNode, $createQuoteNode, HeadingNode, QuoteNode } from "@lexical/rich-text";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { PASTE_COMMAND } from "lexical";
import { $setBlocksType } from "@lexical/selection";

const theme = {
  ltr: "ltr",
  rtl: "rtl",
  placeholder: "editor-placeholder",
  paragraph: "editor-paragraph",
  quote: "editor-quote",
  heading: {
    h1: "editor-heading-h1",
    h2: "editor-heading-h2",
    h3: "editor-heading-h3",
  },
  list: {
    nested: {
      listitem: "editor-nested-listitem",
    },
    ol: "editor-list-ol",
    ul: "editor-list-ul",
    listitem: "editor-listitem",
  },
  image: "editor-image",
  link: "editor-link",
  text: {
    bold: "editor-text-bold",
    italic: "editor-text-italic",
    underline: "editor-text-underline",
    strikethrough: "editor-text-strikethrough",
    underlineStrikethrough: "editor-text-underlineStrikethrough",
  },
};

const removeInvisibleArtifacts = (text) =>
  Array.from(text)
    .filter((char) => {
      const code = char.charCodeAt(0);
      return !(
        (code >= 0 && code <= 8) ||
        code === 11 ||
        code === 12 ||
        (code >= 14 && code <= 31) ||
        (code >= 127 && code <= 159) ||
        (code >= 0x200b && code <= 0x200d) ||
        code === 0xfeff
      );
    })
    .join("");

// Paste Sanitization Plugin
function PasteSanitizationPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (selection !== null && event instanceof ClipboardEvent) {
          const clipboardData = event.clipboardData;
          if (clipboardData !== null) {
            // FORCE PLAIN TEXT VERSION
            const text = clipboardData.getData("text/plain");
            if (text) {
              // Aggressively trim leading '?' and invisible artifacts
              // (often Byte Order Mark or non-breaking spaces)
              const sanitizedText = text
                .replace(/^[\s\uFEFF\u200B\u200C\u200D\xA0?]+/, "");
              const cleanedText = removeInvisibleArtifacts(sanitizedText);

              // Create paragraph nodes for each line to maintain structure
              const lines = cleanedText.split(/\r?\n/);
              const nodes = [];
              lines.forEach((line) => {
                const paragraph = $createParagraphNode();
                paragraph.append($createTextNode(line));
                nodes.push(paragraph);
              });

              selection.insertNodes(nodes);
              event.preventDefault();
              return true;
            }
          }
        }
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor]);

  return null;
}

function LinkCommandPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([LinkNode])) {
      throw new Error("LinkCommandPlugin: LinkNode not registered on editor");
    }

    return editor.registerCommand(
      TOGGLE_LINK_COMMAND,
      (payload) => {
        $toggleLink(payload);
        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  return null;
}

// Toolbar Plugin
function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = React.useState(false);
  const [isItalic, setIsItalic] = React.useState(false);
  const [isUnderline, setIsUnderline] = React.useState(false);
  const [isStrikethrough, setIsStrikethrough] = React.useState(false);
  const [isLink, setIsLink] = React.useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (selection) {
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));

      const node = selection.getNodes()[0];
      const parent = node?.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor, updateToolbar]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const clearFormatting = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (selection) {
        selection.getNodes().forEach((node) => {
          if (node.setFormat) {
            node.setFormat(0);
          }
          if (node.setStyle) {
            node.setStyle("");
          }
        });
      }
    });
  }, [editor]);

  const formatBlock = useCallback(
    (blockType) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        if (blockType === "paragraph") {
          $setBlocksType(selection, () => $createParagraphNode());
        } else if (blockType === "quote") {
          $setBlocksType(selection, () => $createQuoteNode());
        } else {
          $setBlocksType(selection, () => $createHeadingNode(blockType));
        }
      });
    },
    [editor]
  );

  return (
    <div className="toolbar">
      <select
        className="toolbar-select"
        defaultValue="paragraph"
        title="Text style"
        onChange={(event) => formatBlock(event.target.value)}
      >
        <option value="paragraph">Normal</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
        <option value="quote">Quote</option>
      </select>
      <div className="divider" />
      <button
        type="button"
        onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
        className="toolbar-item"
        title="Undo"
      >
        <i className="fa fa-undo" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
        className="toolbar-item"
        title="Redo"
      >
        <i className="fa fa-repeat" />
      </button>
      <div className="divider" />
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
        className={"toolbar-item " + (isBold ? "active" : "")}
        title="Bold"
      >
        <i className="fa fa-bold" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
        className={"toolbar-item " + (isItalic ? "active" : "")}
        title="Italic"
      >
        <i className="fa fa-italic" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
        className={"toolbar-item " + (isUnderline ? "active" : "")}
        title="Underline"
      >
        <i className="fa fa-underline" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")}
        className={"toolbar-item " + (isStrikethrough ? "active" : "")}
        title="Strikethrough"
      >
        <i className="fa fa-strikethrough" />
      </button>
      <button
        type="button"
        onClick={insertLink}
        className={"toolbar-item " + (isLink ? "active" : "")}
        title="Link"
      >
        <i className="fa fa-link" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
        className="toolbar-item"
        title="Bulleted List"
      >
        <i className="fa fa-list-ul" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
        className="toolbar-item"
        title="Numbered List"
      >
        <i className="fa fa-list-ol" />
      </button>
      <div className="divider" />
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left")}
        className="toolbar-item"
        title="Align Left"
      >
        <i className="fa fa-align-left" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center")}
        className="toolbar-item"
        title="Align Center"
      >
        <i className="fa fa-align-center" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right")}
        className="toolbar-item"
        title="Align Right"
      >
        <i className="fa fa-align-right" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)}
        className="toolbar-item"
        title="Outdent"
      >
        <i className="fa fa-outdent" />
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)}
        className="toolbar-item"
        title="Indent"
      >
        <i className="fa fa-indent" />
      </button>
      <div className="divider" />
      <button
        type="button"
        onClick={clearFormatting}
        className="toolbar-item"
        title="Clear Formatting"
      >
        <i className="fa fa-eraser" />
      </button>
    </div>
  );
}

// Custom HTML Plugin for handling value and onChange
function HTMLPlugin({ value, onChange }) {
  const [editor] = useLexicalComposerContext();
  const lastHtmlRef = React.useRef(value || "");

  // Update editor if value changes from outside
  useEffect(() => {
    if (value !== lastHtmlRef.current) {
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(value || "", "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      });
      lastHtmlRef.current = value || "";
    }
  }, [editor, value]);

  return (
    <OnChangePlugin
      onChange={(editorState) => {
        editorState.read(() => {
          const htmlString = $generateHtmlFromNodes(editor, null);
          if (htmlString !== lastHtmlRef.current) {
            lastHtmlRef.current = htmlString;
            if (onChange) {
              onChange(htmlString);
            }
          }
        });
      }}
    />
  );
}

const editorConfig = {
  namespace: "LexicalEditor",
  theme,
  onError(error) {
    console.error(error);
  },
  nodes: [
    HeadingNode,
    QuoteNode,
    ListNode,
    ListItemNode,
    LinkNode,
    AutoLinkNode,
  ],
};

const LexicalEditor = ({ value, onChange }) => {
  return (
    <div className="lexical-editor-container">
      <LexicalComposer initialConfig={editorConfig}>
        <div className="editor-inner">
          <ToolbarPlugin />
          <div className="editor-content-wrapper" style={{ position: 'relative' }}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable className="editor-input" />
              }
              placeholder={
                <div className="editor-placeholder">Type your message here...</div>
              }
            />
            <HistoryPlugin />
            <ListPlugin />
            <LinkCommandPlugin />
            <PasteSanitizationPlugin />
            <HTMLPlugin value={value} onChange={onChange} />
          </div>
        </div>
      </LexicalComposer>
      <style>{`
        .lexical-editor-container {
          position: relative;
          border: 1px solid rgba(226, 232, 240, 1);
          border-radius: 12px;
          background: #ffffff;
          color: #0f172a;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        .toolbar {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 4px;
          padding: 8px;
          border-bottom: 1px solid rgba(226, 232, 240, 0.9);
          background: #f8fafc;
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
        }
        .toolbar-item {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          border-radius: 6px;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s;
        }
        .toolbar-select {
          height: auto !important;
          min-height: 35px !important;
          line-height: normal !important;
          vertical-align: middle !important;
          min-width: 118px !important;
          border: 1px solid #dbe3ef !important;
          border-radius: 6px !important;
          background: #ffffff !important;
          color: #334155 !important;
          font-size: 13px !important;
          font-weight: 700 !important;
          padding: 5px 24px 5px 8px !important;
          outline: none !important;
          box-sizing: border-box !important;
        }
        .toolbar-select:focus {
          border-color: #93c5fd;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }
        .toolbar-item:hover {
          background: rgba(37, 99, 235, 0.1);
          color: #2563eb;
        }
        .toolbar-item.active {
          background: #e0e7ff;
          color: #4338ca;
        }
        .divider {
          width: 1px;
          height: 24px;
          background: #e2e8f0;
          margin: 4px 8px;
        }
        .editor-inner {
          position: relative;
        }
        .editor-input {
          min-height: 150px;
          padding: 12px;
          outline: none;
          overflow-y: auto;
        }
        .editor-placeholder {
          position: absolute;
          top: 12px;
          left: 12px;
          color: #94a3b8;
          pointer-events: none;
          margin: 0;
        }
        .editor-paragraph {
          margin-bottom: 8px;
          line-height: 1.6;
        }
        .editor-link {
          color: #2563eb;
          text-decoration: underline;
        }
        .editor-quote {
          margin: 8px 0;
          padding-left: 12px;
          border-left: 3px solid #c7d2fe;
          color: #475569;
          font-style: italic;
        }
        .editor-heading-h1 {
          font-size: 24px;
          font-weight: 800;
          line-height: 1.25;
          margin: 8px 0;
        }
        .editor-heading-h2 {
          font-size: 20px;
          font-weight: 800;
          line-height: 1.3;
          margin: 8px 0;
        }
        .editor-heading-h3 {
          font-size: 17px;
          font-weight: 800;
          line-height: 1.35;
          margin: 8px 0;
        }
        .editor-list-ul {
          padding-left: 20px;
          margin: 0;
          list-style-type: disc;
        }
        .editor-list-ol {
          padding-left: 20px;
          margin: 0;
          list-style-type: decimal;
        }
        .editor-listitem {
          margin: 4px 0;
        }
        .editor-text-bold { font-weight: bold; }
        .editor-text-italic { font-style: italic; }
        .editor-text-underline { text-decoration: underline; }
        .editor-text-strikethrough { text-decoration: line-through; }
        .editor-text-underlineStrikethrough { text-decoration: underline line-through; }
      `}</style>
    </div>
  );
};

export default LexicalEditor;
